import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Save, FileSpreadsheet, Trash2, Replace, Share2 } from "lucide-react";
import { exportPortfolioToCSV } from "@/utils/exportUtils";
import { ComboButton } from "@/components/ui/ComboButton";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { convertPortfolioToModel } from "@/services/portfolio/PortfolioService";
import { toast } from "sonner";

interface PortfolioActionsProps {
  onDeleteClick: () => void;
  onSaveClick: () => Promise<void>;
  isDeleteDialogOpen: boolean;
  setIsDeleteDialogOpen: (open: boolean) => void;
  portfolio?: any; // Using any here as we don't have the full type, but in practice should be properly typed
}

export function PortfolioActions({
  onDeleteClick,
  onSaveClick,
  isDeleteDialogOpen,
  setIsDeleteDialogOpen,
  portfolio
}: PortfolioActionsProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const handleExport = () => {
    console.log('Export button clicked');
    console.log('Portfolio data:', portfolio);
    
    if (portfolio) {
      try {
        console.log('Attempting to export portfolio:', {
          name: portfolio.name,
          size: portfolio.portfolio_size,
          allocations: portfolio.portfolio_allocations
        });
        exportPortfolioToCSV(portfolio);
        console.log('Export completed successfully');
      } catch (error) {
        console.error('Error exporting portfolio:', error);
      }
    } else {
      console.warn('No portfolio data available for export');
    }
  };

  const isConvertibleType = (portfolio?.portfolio_type === 'personal' || portfolio?.portfolio_type === 'client');
  const hasDollar = Array.isArray(portfolio?.portfolio_allocations) && (portfolio.portfolio_allocations as any[])?.some(a => (Number(a?.allocation_value) || 0) > 0);

  const handleConvert = async () => {
    if (!isConvertibleType || !hasDollar) return;
    const uid = user?.id;
    if (!uid) return;
    const newId = await convertPortfolioToModel({ originalId: portfolio.id, userId: uid });
    navigate(`/portfolios/build/${newId}`);
  };

  const handleShare = async () => {
    try {
      const url = window.location.href;
      const shareData = {
        title: portfolio?.name || 'Portfolio',
        text: 'Check out this portfolio on Superalts',
        url,
      } as any;
      if ((navigator as any).share) {
        await (navigator as any).share(shareData);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
      } else {
        toast.error('Sharing is not supported on this device');
      }
    } catch (e) {
      // Silent cancel
    }
  };

  return (
    <div className="flex justify-between items-center">
      <Button
        variant="ghost"
        onClick={() => setIsDeleteDialogOpen(true)}
        className="gap-2 text-gray-600 hover:text-destructive hover:bg-destructive/10 [&_svg]:hover:text-destructive"
      >
        <Trash2 className="h-4 w-4" />
        Delete Portfolio
      </Button>

      <div className="flex gap-1">
        <ComboButton
          label="Save Portfolio"
          icon={<Save className="h-4 w-4" />}
          onPrimaryClick={onSaveClick}
          size="sm"
          variant="outline"
          includePrimaryInMenu
          menuItems={[
            { label: "Share Portfolio", onClick: handleShare, icon: <Share2 className="h-4 w-4" /> },
            { label: "Export to Excel", onClick: handleExport, icon: <FileSpreadsheet className="h-4 w-4" /> },
            { label: "Convert to Model", onClick: handleConvert, icon: <Replace className="h-4 w-4" />, disabled: !(isConvertibleType && hasDollar) },
          ]}
        />
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your
              portfolio and remove all data associated with it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onDeleteClick();
                setIsDeleteDialogOpen(false);
              }}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}